import React, { useState, useRef, useEffect } from 'react';
import { GoogleGenerativeAI } from "@google/generative-ai";
import { HoverBorderGradient } from './components/ui/hover-border-gradient.tsx';
import chatbotData from "./Data/ChatbotData.js";
import { Input } from './components/ui/input.tsx';
import { MessageCircleMore, X, BotMessageSquare } from "lucide-react";

const ChatBot = () => {
  // To handle the opening and closing of the chat box
  const [isOpen, setIsOpen] = useState(false);
  // State to record conversation
  const [messages, setMessages] = useState([]);
  // State variable tp hold the input message typed by the user
  const [inputMessage, setInputMessage] = useState('');
  //To handle the visibility of the typing indicator
  const [isTyping, setIsTyping] = useState(false);
  // To control the ping animation of the chatbot icon
  const [chatAnimation, setChatAnimation] = useState(true);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  const suggestedQuestions = [
    "What values are important to Tshepo in a workplace?",
    "Can you describe a work environment where Tshepo feels most productive?",
    "Where does Tshepo see himself professionally in the next 3-5 years?"

  ];

  const convertUrlsToLinks = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, index) =>
      urlRegex.test(part) ? (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer" className="text-white underline hover:text-cyan-300">
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  const handleSuggestedQuestion = (question) => {
    setInputMessage(question);
    sendMessage(question);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const userMessage = { role: 'user', content: inputMessage };
    // Adding the user's input to the conversation's state
    setMessages([...messages, userMessage]);
    setInputMessage('');
    setIsTyping(true); // Show typing indicator

    try {
      const apiKey = process.env.REACT_APP_GEMINI_API_KEY;
      const genAI = new GoogleGenerativeAI(apiKey);
      const model = genAI.getGenerativeModel({
        model: "gemini-2.5-flash",
        systemInstruction: chatbotData,
      });

      const result = await model.generateContent(inputMessage);
      // Storing the generated response in an object
      const botMessage = { role: 'bot', content: result.response.text() };
      // Adding the response to the conversation
      setMessages(prevMessages => [...prevMessages, botMessage]);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setIsTyping(false); // to hide typing indicator
    }
  };

  return (
    <div className={`fixed z-50 ${chatAnimation 
        ? "bottom-[-20px] right-[-20px] animate-wiggle hover:animate-none" 
        : "bottom-4 right-4"}`}>
      {!isOpen && (
        <button
          onClick={() => {
            setIsOpen(true);
            setChatAnimation(false);
          }
          }
          className={`transition-all duration-500 bg-black hover:bg-gradient-to-r from-cyan-300 to-violet-700 text-white ${chatAnimation ? "rounded-[55px] p-6" : "rounded-full p-3"} shadow-lg`}
        >
        <MessageCircleMore />
        </button>
      )}
      {isOpen && (
        <div className="bg-white rounded-lg shadow-2xl bg-gray-20 w-80 h-[65vh] flex flex-col">
          <div className="bg-gradient-to-r from-cyan-300 to-violet-700 text-white p-4 rounded-t-lg flex justify-between items-center">
            <h3 className="font-bold">Tshepo's Companion</h3>
            <button onClick={() => setIsOpen(false)} className="text-white">
              <X />
            </button>
          </div>
          <div className="flex-1 overflow-y-scroll p-4">

            {/* Suggested questions within the chatbox */}
            <div className="p-2 mb-12">
              <div className="bg-violet-700 border-l-4 border-violet-900 text-white p-4 mb-4 rounded-md shadow-md">
                <p className="font-medium">AI Chatbot Disclaimer</p>
                <p className="text-sm">
                  This is an AI-powered chatbot. It may make mistakes or misunderstand queries.
                </p>
              </div>
              <p className="text-sm text-gray-600 mb-2">Questions you can ask:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedQuestions.map((question, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestedQuestion(question)}
                    className="text-xs bg-gray-200 hover:bg-gray-300 rounded px-2 py-1 transition-colors duration-200"
                  >
                    {question}
                  </button>
                ))}
              </div>
            </div>

            {/* Conversation between assistant and the user */}
            {messages.map((message, index) => (
              <div key={index} className={`mb-2 flex w-fit ${message.role === 'user' ? 'ml-16 float-right' : 'mr-6 float-left'}`}>
                {message.role === 'bot' && <div className='fit-content min-w-8 rounded-full mr-2'><BotMessageSquare /></div>}
                <span className={`inline-block p-2 max-w-52 hyphens-auto overflow-hidden whitespace-normal rounded-lg ${message.role === 'user' ? 'bg-cyan-300 rounded-tr-none text-black' : 'bg-violet-700 rounded-tl-none text-white'} `}>
                  {convertUrlsToLinks(message.content)}
                </span>
              </div>
            ))}
            {isTyping && (
              <div className="flex float-end mr-56 items-center space-x-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-black animate-bounce"></div>
                <div className="w-2 h-2 rounded-full bg-cyan-300 animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 rounded-full bg-violet-700 animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            )}
            <div className="float-end" ref={messagesEndRef} />
          </div>
          <div className="p-4 border-t">
            <div className="flex place-content-between">
              {/* <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                className="flex-1 focus:border-b-2 focus:outline-none hover:border-b-2 rounded-l-lg p-2"
                placeholder="Ask your question here..."
              /> */}
              <Input 
              placeholder="Ask your question here..."
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
              />
              <HoverBorderGradient>
                <button
                  onClick={sendMessage}
                >
                  Send
                </button>
              </HoverBorderGradient>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatBot;